import { InjectionToken } from '@angular/core';

/**
 * What the shared sign-in page needs to know about the app hosting it.
 *
 * THE LINE TO HOLD: **values are inputs, structure is not.** Both apps render the same DOM from
 * `sign-in.component.html`; only these six strings differ. The day the two doors need different
 * *structure* — an SSO button juno must not have, a second factor argus must not have — fork the
 * html. Do not add a seventh field to switch a block on and off. That is how one shared page turns
 * back into two divergent ones, which is what it was written to stop (docs/ui/sign-in.md §9).
 *
 * Provided once per app in `app.config.ts`, beside the existing VERSION_INFO provider.
 */
export interface AppInfo {
  /** The app's own name, the h1. 'Juno' | 'Argus'. */
  name: string;
  /**
   * What kind of work happens here, three words. Names the DOMAIN and stops: the page is
   * unauthenticated, so anyone who finds the URL reads this (docs/ui/sign-in.md §4.1).
   */
  purpose: string;
  /**
   * Which credential population this door accepts. The single most useful string on the page:
   * juno signs in Friday Tech employees, argus signs in salon staff, and the wrong one gets a
   * bare 401 from identity with no explanation.
   */
  credentialHint: string;
  /** Where a successful sign-in lands. '/businesses' | '/customers'. */
  homePath: string;
  /**
   * The brand half: who the app is named for, and why anyone should care. This is the one place
   * either console says something that is not operational, and it is deliberate — it is for the
   * Sales team and the AMs who open this door every morning, not for whoever finds the URL.
   *
   * Words only, no portrait (VT, 2026-09-04). A museum photograph was tried and did not sit in a
   * flat white page; making one that does is illustration work, not engineering. The story
   * carries the pane on its own (docs/ui/sign-in.md §2.4).
   *
   * It discloses nothing: it says nothing about what the app does, which environment it points at,
   * or who may sign in. That is why it survives the rule that cut the purpose line to three words.
   */
  lore: {
    /** The god's full name; the brand half's heading. */
    title: string;
    /** Two or three short sentences. Keep them true — the etymology is the point. */
    story: string;
  };
}

export const APP_INFO = new InjectionToken<AppInfo>('APP_INFO');
